import React, { useRef, useState } from 'react';
import { NtIcon, Pill, NT_COLORS } from './NotionUI';
import { useLanguage } from '../i18n/LanguageContext';
import { uploadProjectTaskFiles, deleteProjectTaskFile, downloadProjectTaskFile, errorMessage } from '../services/api';
import { fmtDateTime } from '../utils/format';
import Select from './Select';
import Highlight from './KernelHighlight';
import { searchTerms, matchesAllTerms } from '../utils/kernelSearch';
import { userColor } from '../utils/userColor';
import { personIds } from '../utils/projectView';

export const optionColor = (option) => NT_COLORS[option?.color] || NT_COLORS.grey;

const userName = (users, id) => {
  const u = users.find((x) => x.id === id);
  return u ? [u.name, u.surname].filter(Boolean).join(' ') : '';
};

/** Pannello flottante ancorato a un elemento (le celle della tabella ritagliano il contenuto, quindi non si usa position: absolute). */
function FloatingPanel({ anchor, onClose, children }) {
  const rect = anchor.current ? anchor.current.getBoundingClientRect() : { left: 0, bottom: 0 };
  const left = Math.min(rect.left, window.innerWidth - 280);
  return (
    <>
      <div className="pd-overlay" onClick={onClose} />
      <div className="pd-popover pd-float" style={{ position: 'fixed', top: rect.bottom + 2, left: Math.max(8, left), width: 260 }}>
        {children}
      </div>
    </>
  );
}

const personName = (u) => [u.name, u.surname].filter(Boolean).join(' ');
const personInitial = (u) => (personName(u)[0] || '?').toUpperCase();

/** Persona con il suo avatar (e "(tu)" se è l'utente corrente). */
function PersonChip({ person, terms }) {
  const { t } = useLanguage();
  return (
    <span className="pc-chip">
      <span className="pc-avatar" style={{ background: userColor(person) }} aria-hidden="true">{personInitial(person)}</span>
      <span className="pc-name"><Highlight text={personName(person)} terms={terms} />{person.isMe && <span className="pc-me"> ({t('projectDetail.you')})</span>}</span>
    </span>
  );
}

/** Colonna "Persona": come Notion, più persone scelte da un riquadro con la ricerca (l'utente corrente col "(tu)"). */
function PersonCell({ value, users, label, onSave }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const ids = personIds(value);
  const selected = ids.map((id) => users.find((u) => u.id === id)).filter(Boolean);
  const terms = searchTerms(query);
  const options = users.filter((u) => matchesAllTerms(personName(u), terms));
  const close = () => { setOpen(false); setQuery(''); };
  const toggle = (person) => {
    const next = ids.includes(person.id) ? ids.filter((id) => id !== person.id) : [...ids, person.id];
    onSave(next.length > 0 ? next : null);
  };

  return (
    <div className="pd-multi" ref={ref}>
      <button type="button" className="pd-multi-btn pc-btn" aria-label={label} onClick={() => setOpen(true)}>
        {selected.length === 0 && <span className="nt-muted">—</span>}
        {selected.map((person) => <PersonChip key={person.id} person={person} terms={[]} />)}
      </button>
      {open && (
        <FloatingPanel anchor={ref} onClose={close}>
          <input
            autoFocus
            value={query}
            placeholder={t('projectDetail.personSearch')}
            aria-label={t('projectDetail.personSearch')}
            onChange={(e) => setQuery(e.target.value)}
          />
          <p className="nt-muted pd-popover-hint pc-hint">{t('projectDetail.personHint')}</p>
          {options.length === 0 && <p className="nt-muted pd-popover-hint">{t('projectDetail.personNone')}</p>}
          {options.map((u) => (
            <button key={u.id} type="button" className={'pc-option' + (ids.includes(u.id) ? ' pc-option-on' : '')} aria-pressed={ids.includes(u.id)} onClick={() => toggle(u)}>
              <PersonChip person={u} terms={terms} />
              {ids.includes(u.id) && <span className="pc-check" aria-hidden="true">✓</span>}
            </button>
          ))}
          {ids.length > 0 && <button type="button" className="pc-clear" onClick={() => onSave(null)}>{t('projectDetail.personClear')}</button>}
        </FloatingPanel>
      )}
    </div>
  );
}

function MultiSelectCell({ column, value, label, onSave }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = Array.isArray(value) ? value : [];
  const toggle = (v) => {
    const next = selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v];
    onSave(next.length ? next : null);
  };
  return (
    <div className="pd-multi" ref={ref}>
      <button type="button" className="pd-multi-btn" aria-label={label} onClick={() => setOpen(true)}>
        {selected.length === 0 && <span className="nt-muted">—</span>}
        {selected.map((v) => {
          const option = column.options.find((o) => o.value === v);
          return option ? <Pill key={v} color={optionColor(option)}>{option.label}</Pill> : null;
        })}
      </button>
      {open && (
        <FloatingPanel anchor={ref} onClose={() => setOpen(false)}>
          <div className="pd-popover-label">{t('projectDetail.selectOptions')}</div>
          {column.options.map((o) => (
            <label key={o.value} className="pd-multi-option">
              <input type="checkbox" checked={selected.includes(o.value)} onChange={() => toggle(o.value)} />
              <Pill color={optionColor(o)}>{o.label}</Pill>
            </label>
          ))}
        </FloatingPanel>
      )}
    </div>
  );
}

function RelationCell({ task, column, value, label, allTasks, onSave }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const selected = Array.isArray(value) ? value : [];
  const terms = searchTerms(query);
  const options = allTasks.filter((x) => x.id !== task.id && matchesAllTerms(x.title || '', terms));
  const toggle = (id) => {
    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    onSave(next.length ? next : null);
  };
  return (
    <div className="pd-multi" ref={ref}>
      <button type="button" className="pd-multi-btn" aria-label={label} onClick={() => { setQuery(''); setOpen(true); }}>
        {selected.length === 0 && <span className="nt-muted">—</span>}
        {selected.map((id) => {
          const other = allTasks.find((x) => x.id === id);
          return other ? <span key={id} className="pd-ticket-chip">{other.title || t('projectDetail.untitled')}</span> : null;
        })}
      </button>
      {open && (
        <FloatingPanel anchor={ref} onClose={() => setOpen(false)}>
          <input autoFocus value={query} placeholder={t('projectDetail.searchTasks')} aria-label={t('projectDetail.searchTasks')} onChange={(e) => setQuery(e.target.value)} />
          {options.length === 0 && <p className="nt-muted pd-popover-hint">{t('projectDetail.noTasksFound')}</p>}
          {options.slice(0, 60).map((other) => (
            <label key={other.id} className="pd-multi-option">
              <input type="checkbox" checked={selected.includes(other.id)} onChange={() => toggle(other.id)} />
              <span><Highlight text={other.title || t('projectDetail.untitled')} terms={terms} /></span>
            </label>
          ))}
        </FloatingPanel>
      )}
    </div>
  );
}

const formatSize = (bytes) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

function FilesCell({ task, column, value, label, onTaskReplace, onError }) {
  const { t } = useLanguage();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const files = Array.isArray(value) ? value : [];

  const upload = async (e) => {
    const picked = [...e.target.files];
    e.target.value = '';
    if (picked.length === 0) return;
    setBusy(true);
    try {
      const data = await uploadProjectTaskFiles(task.id, column.id, picked);
      onTaskReplace(data.task);
    } catch (err) {
      onError(errorMessage(err, t('projectDetail.uploadError')));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (file) => {
    try {
      const data = await deleteProjectTaskFile(task.id, file.id);
      onTaskReplace(data.task);
    } catch (err) {
      onError(errorMessage(err, t('projectDetail.saveError')));
    }
  };

  return (
    <div className="pd-files" aria-label={label}>
      {files.map((file) => (
        <span key={file.id} className="pd-file-chip" title={`${file.name} (${formatSize(file.size)})`}>
          <button type="button" className="pd-file-name" onClick={() => downloadProjectTaskFile(task.id, file).catch((err) => onError(errorMessage(err, t('projectDetail.saveError'))))}>
            <NtIcon name="paperclip" /> {file.name}
          </button>
          <button type="button" className="pd-file-remove" aria-label={t('projectDetail.removeFile')} onClick={() => remove(file)}>×</button>
        </span>
      ))}
      <input ref={inputRef} type="file" multiple hidden onChange={upload} />
      <button type="button" className="pd-row-delete pd-ticket-open" style={{ opacity: 1 }} disabled={busy} onClick={() => inputRef.current.click()} aria-label={t('projectDetail.addFile')} title={t('projectDetail.addFile')}>
        {busy ? '…' : '+'}
      </button>
    </div>
  );
}

/**
 * Cella modificabile di un'attività in base al tipo di colonna. Le celle di
 * testo salvano all'uscita dal campo (blur/Invio), le altre alla modifica.
 * `onSave(valore)`: null svuota la cella. `onTaskReplace(task)` serve alle celle
 * che cambiano l'attività per altra via (file).
 */
function ProjectCell({ task, column, users, allTasks = [], onSave, onTaskReplace, onError }) {
  const { t } = useLanguage();
  const value = task.data[String(column.id)];
  const label = `${column.name}: ${task.title}`;

  switch (column.type) {
    case 'text':
    case 'url':
    case 'email':
    case 'phone':
      return (
        <div className="pd-cell-row">
          <input
            key={`${task.id}:${value ?? ''}`}
            className="pd-input"
            type={column.type === 'email' ? 'email' : 'text'}
            defaultValue={value ?? ''}
            aria-label={label}
            onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
            onBlur={(e) => e.target.value.trim() !== (value ?? '') && onSave(e.target.value.trim())}
          />
          {value && column.type !== 'text' && (
            <a
              className="pd-link"
              href={column.type === 'email' ? `mailto:${value}` : (column.type === 'phone' ? `tel:${value}` : value)}
              target={column.type === 'url' ? '_blank' : undefined}
              rel="noopener noreferrer"
              aria-label={t('projectDetail.openLink')}
            >
              <NtIcon name={column.type === 'email' ? 'mail' : (column.type === 'phone' ? 'phone' : 'link')} />
            </a>
          )}
        </div>
      );
    case 'number':
      return (
        <input
          key={`${task.id}:${value ?? ''}`}
          className="pd-input pd-input-number"
          type="number"
          step="any"
          defaultValue={value ?? ''}
          aria-label={label}
          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
          onBlur={(e) => {
            const raw = e.target.value.trim();
            if (raw !== String(value ?? '')) onSave(raw === '' ? null : Number(raw));
          }}
        />
      );
    case 'date':
      return (
        <input
          className="pd-input"
          type="date"
          value={value ?? ''}
          aria-label={label}
          onChange={(e) => onSave(e.target.value || null)}
        />
      );
    case 'daterange': {
      const range = value || {};
      const change = (edge, v) => {
        const next = { ...range, [edge]: v || undefined };
        // La fine non può precedere l'inizio: se serve la si allinea.
        if (next.start && next.end && next.end < next.start) next[edge === 'start' ? 'end' : 'start'] = next[edge];
        onSave(next.start || next.end ? { start: next.start, end: next.end } : null);
      };
      return (
        <div className="pd-range">
          <input className="pd-input" type="date" value={range.start ?? ''} aria-label={`${label} (${t('projectDetail.rangeStart')})`} onChange={(e) => change('start', e.target.value)} />
          <span className="nt-muted" aria-hidden="true">→</span>
          <input className="pd-input" type="date" value={range.end ?? ''} aria-label={`${label} (${t('projectDetail.rangeEnd')})`} onChange={(e) => change('end', e.target.value)} />
        </div>
      );
    }
    case 'checkbox':
      return (
        <label className="pd-check">
          <input type="checkbox" checked={Boolean(value)} aria-label={label} onChange={(e) => onSave(e.target.checked ? true : null)} />
        </label>
      );
    case 'select':
    case 'status': {
      const current = column.options.find((o) => o.value === value);
      return (
        <Select
          className={'dd-cell' + (current ? ' dd-cell-set' : '')}
          pill
          value={value ?? ''}
          aria-label={label}
          onChange={(e) => onSave(e.target.value || null)}
        >
          <option value="">—</option>
          {column.options.map((o) => (
            <option key={o.value} value={o.value} data-color={optionColor(o)}>{o.label}</option>
          ))}
        </Select>
      );
    }
    case 'multi_select':
      return <MultiSelectCell column={column} value={value} label={label} onSave={onSave} />;
    case 'person':
      return <PersonCell value={value} users={users} label={label} onSave={onSave} />;
    case 'task_relation':
      return <RelationCell task={task} column={column} value={value} label={label} allTasks={allTasks} onSave={onSave} />;
    case 'files':
      return <FilesCell task={task} column={column} value={value} label={label} onTaskReplace={onTaskReplace} onError={onError} />;
    case 'created_time':
      return <span className="pd-computed nt-muted">{fmtDateTime(task.created_at)}</span>;
    case 'updated_time':
      return <span className="pd-computed nt-muted">{fmtDateTime(task.updated_at)}</span>;
    case 'created_by':
      return <span className="pd-computed nt-muted">{userName(users, task.created_by_user_id)}</span>;
    case 'updated_by':
      return <span className="pd-computed nt-muted">{userName(users, task.updated_by_user_id)}</span>;
    default:
      return null;
  }
}

export default ProjectCell;
